import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { validToUpload } from "./fixtures/validToUpload.ts";
import { useUploadFileToVault } from "../../hooks/vault/mutations/useUploadFileToVault.ts";
import { FileDrop } from "./inputs/FileDrop.tsx";
import { InvalidRequirementReason } from "./InvalidRequirementReason.tsx";
import { UploadedFiles } from "./UploadedFiles.tsx";
import { Status } from "../../redux/interfaces/folder.types.ts";
import { CancelToReview } from "./CancelToReview.tsx";
import { setActivePanels } from "../../redux/slices/vaultSlice.ts";
import { getDocumentUploadPolicy } from "../../constants/uploadPolicies.ts";

interface Props {
  header: React.ReactNode;
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
}

export const GeneralDocument: React.FC<Props> = ({
  header,
  onRegisterSubmit,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { activeRequirement, activeVault } = useAppSelector(
    (state) => state.vault,
  );
  const enabled = validToUpload.includes(activeRequirement?.status);
  const label = activeRequirement?.name || "";

  const dispatch = useAppDispatch();
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const uploadPolicy = getDocumentUploadPolicy({
    requirementSlug: activeRequirement?.slug,
    requirementStatus: activeRequirement?.status,
  });
  const onUploadDocument = useCallback(
    async (file: File): Promise<boolean> => {
      try {
        const formData = new FormData();
        formData.append("files", file);
        await uploadMutation.mutateAsync({
          reqId: activeRequirement!.id,
          formData,
          uploadPolicy,
          replaceCurrentEvidence: true,
        });

        dispatch(setActivePanels([]));
        toast.success(`${label} subido correctamente.`);
        setSelectedFile(null);
        return true;
      } catch (error: any) {
        toast.error(error?.message ?? String(error));
        return false;
      }
    },
    [activeRequirement, dispatch, label, uploadMutation, uploadPolicy],
  );

  // Submit function for external triggering (e.g., from "Siguiente" button)
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = async (): Promise<boolean> => {
    if (!selectedFile) {
      toast.error("Por favor sube un documento");
      return false;
    }
    return onUploadDocument(selectedFile);
  };

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => submitFnRef.current?.() ?? false;
    onRegisterSubmit(fn);
    return () => onRegisterSubmit(null);
  }, [onRegisterSubmit]);

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
  }, []);

  return (
    <div className="w-full p-4 border-none">
      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      <InvalidRequirementReason requirement={activeRequirement!} />

      <div className="flex flex-col md:flex-row">
        {header}

        <div className="w-full md:w-1/2 flex flex-col justify-center items-center space-y-4">
          <FileDrop
            name="general"
            label={label}
            type="both"
            maxFileBytes={uploadPolicy.max_file_bytes}
            maxImageBytes={uploadPolicy.max_image_bytes}
            onFileSelect={handleFileSelect}
          />

          {/* Selected file indicator */}
          {selectedFile && (
            <div className="w-full flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
              <div className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
              <p className="text-sm text-green-700 truncate flex-1">
                {selectedFile.name}
              </p>
              <button
                type="button"
                onClick={() => setSelectedFile(null)}
                className="text-xs text-green-600 hover:text-green-800 font-medium flex-shrink-0"
              >
                Quitar
              </button>
            </div>
          )}

          <UploadedFiles />

          {/* Cancel to review (when already submitted) */}
          {!enabled && activeRequirement?.status === Status.PendingReview && (
            <CancelToReview requirement={activeRequirement!} />
          )}
        </div>
      </div>
    </div>
  );
};
